import React, { useRef, useState, useCallback, useEffect } from "react";
import { CampaignData } from "../../types";
import { EmailEditorCore, EmailEditorCoreRef } from "../../shared/email-editor-core";
import { designFromHtml, htmlToPlainText, parseEmailDesign, restoreVariableNames } from "../../shared/email-design";
import { Plus, Eye } from "lucide-react";
import ConvoAIPanel from "./convoai-hub";
import { toast } from "sonner";
import { TemplateCreator } from "../templates/create-template";
import { TemplateModule } from "../../../views/marketing-pages/templates";
import useRequest from "../../hook/use-req";
import { PlainTextEditor } from "../../shared/plain-text-editor";
import ScrollToTop from "../../../routes/scroll-top";
import { EmailMockUp } from "./email-mockup";

// ─── Types ────────────────────────────────────────────────────────────────────
type TabMode = "plain" | "editor" | "templates";
type ViewMode = "desktop" | "mobile";
type SidebarMode = "ai" | "preview";

interface CampaignAiProps {
  campaignData: CampaignData;
  setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>;
  onSubmit: (data: Partial<CampaignData>) => void;
  onValidation: (isValid: boolean) => void;
  onBack?: () => void;
}

interface EmailTemplate {
  id: string;
  template_name: string;
  subject?: string;
  html?: string;
  plain_text?: string;
  // The template's editor design (its `content`), when it was built in the
  // Email Editor — opens as editable blocks instead of one HTML block.
  design?: object | null;
  thumbnail?: string | null;
  snapshot_url: string | null;
}

const EMAIL_VARIABLE_OPTIONS = [
  "first_name", "last_name", "email", "phone", "company_name", "lead_status", "lead_score",
];

// ─── Template Card ────────────────────────────────────────────────────────────
const TemplateCard: React.FC<{
  template: EmailTemplate;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ template, isSelected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`w-full text-left rounded-xl border-2 overflow-hidden transition-all hover:shadow-md bg-white dark:bg-gray-900 ${
      isSelected ? "border-indigo-500 ring-2 ring-indigo-200" : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
    }`}
  >
    <div className="bg-gray-100 dark:bg-gray-800 h-36 flex items-center justify-center border-b border-gray-200 dark:border-gray-700 relative overflow-hidden">
      {template.snapshot_url ? (
        <img src={template.snapshot_url} alt={template.template_name} className="w-full h-full object-cover" />
      ) : (
        <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">No Preview Available</span>
      )}
      {isSelected && (
        <div className="absolute top-2 right-2 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
          <span className="text-white text-[10px]">✓</span>
        </div>
      )}
    </div>
    <div className="px-3 py-2">
      <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{template.template_name}</p>
    </div>
  </button>
);

// ─── Helper ───────────────────────────────────────────────────────────────
const extractPlainTextFromHtml = (html: string): string => {
  if (!html) return "";
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  tempDiv.querySelectorAll("style, script").forEach((el) => el.remove());
  let text = tempDiv.textContent || tempDiv.innerText || "";
  text = text.replace(/\s+/g, " ").replace(/\n\s*\n/g, "\n\n").trim();
  return text;
};

// ─── Main Component ───────────────────────────────────────────────────────────
export const EmailContentCreation: React.FC<CampaignAiProps> = ({
  campaignData,
  setCampaignData,
  onSubmit,
  onValidation,
  onBack,
}) => {
  const editorRef = useRef<EmailEditorCoreRef | null>(null);
  const token = localStorage.getItem("authToken");

  // Re-mounting this step (e.g. after clicking Back and forward again) used
  // to always land on "Plain Text" even if a template was already chosen —
  // derive the starting tab from campaignData instead of a fixed default so
  // navigating back stays on the Templates tab, previewing what was picked.
  const [activeTab, setActiveTab] = useState<TabMode>(campaignData?.template_id ? "templates" : "plain");
  const [viewMode, setViewMode] = useState<ViewMode>("desktop");
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>("preview");

  const [emailSubject, setEmailSubject] = useState<string>(campaignData?.subject || "");
  const [emailHeader, setEmailHeader] = useState<string>(campaignData?.email_header || "");
  const [htmlText, setHtmlText] = useState<string>(campaignData?.html_text || "");
  const [editorHtml, setEditorHtml] = useState<string>(campaignData?.html_text || "");
  const [emailPlainText, setEmailPlainText] = useState<string>(campaignData?.plain_text || "");
  const [hasEditorContent, setHasEditorContent] = useState<boolean>(!!campaignData?.html_text);
  // Latest editor design — from the picked template, then every edit — so
  // reopening the editor restores blocks, not a single HTML block.
  const designRef = useRef<object | null>(null);

  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [templateSearch, setTemplateSearch] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(campaignData?.template_id || null);
  const [showVariablePicker, setShowVariablePicker] = useState(false);
  const [customVariable, setCustomVariable] = useState("");
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showCreator, setShowCreator] = useState(false);
  const [templates, setTemplates] = useState<TemplateModule[]>([]);
  const [showTestModal, setShowTestModal] = useState(false);

  const { makeRequest: getTemplates } = useRequest("/templates/", "GET", {
    Authorization: `Bearer ${token}`,
  });

  const convertHtmlToPlainText = useCallback((html: string): string => {
    if (!html) return "";
    return extractPlainTextFromHtml(html);
  }, []);

  // Sync data
  useEffect(() => {
    const currentHtml = activeTab === "editor" ? (htmlText || editorHtml) : htmlText;
    const plain = emailPlainText || convertHtmlToPlainText(currentHtml);

    setCampaignData((prev) => ({
      ...prev,
      subject: emailSubject,
      email_header: emailHeader,
      html_text: currentHtml,
      messages: plain,
      plain_text: plain,
      template_id: selectedTemplateId || prev.template_id || "",
    }));
  }, [emailSubject, emailHeader, htmlText, editorHtml, emailPlainText, selectedTemplateId, activeTab, setCampaignData, convertHtmlToPlainText]);

  // ─── Validation ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const hasSubject = emailSubject.trim().length > 0;
    let hasContent = false;

    if (activeTab === "plain") {
      hasContent = emailPlainText.trim().length > 0;
    } else if (activeTab === "editor") {
      const currentHtml = htmlText || editorHtml;
      hasContent = currentHtml.trim().length > 10 || hasEditorContent;
    } else if (activeTab === "templates") {
      hasContent = htmlText.trim().length > 0 || hasEditorContent;
    }

    onValidation(hasSubject && hasContent);
  }, [emailSubject, htmlText, editorHtml, emailPlainText, activeTab, onValidation, hasEditorContent]);

  // ─── Editor Handlers ────────────────────────────────────────────────────────
  const handleEditorChange = useCallback((html: string, design?: object) => {
    if (design) designRef.current = design;
    setEditorHtml(html);
    setHtmlText(html);
    if (html && html.trim().length > 10) {
      setHasEditorContent(true);
    } else {
      setHasEditorContent(false);
    }
  }, []);

  const handleEditorReady = useCallback(() => {
    const existingHtml = campaignData?.html_text || htmlText || editorHtml;
    const design = designRef.current || (existingHtml ? designFromHtml(existingHtml) : null);
    if (design) {
      setTimeout(() => {
        editorRef.current?.loadDesign(design);
        setHasEditorContent(true);
      }, 300);
    }
  }, [campaignData?.html_text, htmlText, editorHtml]);

  const handleTemplateGenerated = useCallback((data: { html: string; subject?: string; header?: string; plainText?: string }) => {
    const cleanPlainText = data.plainText || convertHtmlToPlainText(data.html);
    setHtmlText(data.html);
    setEditorHtml(data.html);
    setEmailPlainText(cleanPlainText);
    if (data.subject) setEmailSubject(data.subject);
    if (data.header) setEmailHeader(data.header);
    setHasEditorContent(true);
  }, [convertHtmlToPlainText]);

  // ─── Templates ──────────────────────────────────────────────────────────────
  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const [response] = await getTemplates(undefined, { channel: "Email" });
      let templatesData: TemplateModule[] = (Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [])
        // Disabled templates can't be used in a campaign.
        .filter((t: TemplateModule) => t?.is_active !== false);
      
      setTemplates(templatesData);
      const mapped = templatesData.map((t: any) => ({
        id: t.id,
        template_name: t.template_name || "Untitled Template",
        subject: t.subject,
        ...(() => {
          // `content` is the editor design for Email Editor templates (with
          // variables saved as {{index}}), plain text otherwise.
          const design = parseEmailDesign(t.content, t.variables);
          const html = restoreVariableNames(t.html_content || (design ? "" : t.content) || "", t.variables);
          return { html, design, plain_text: design ? htmlToPlainText(html) : t.content || "" };
        })(),
        snapshot_url: t.snapshot_url ?? "",
      }));
      setEmailTemplates(mapped);
    } catch (error) {
      toast.error("Failed to load templates");
      setEmailTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "templates") fetchTemplates();
  }, [activeTab, fetchTemplates]);

  const handleSelectEmailTemplate = (tmpl: EmailTemplate) => {
    setSelectedTemplateId(tmpl.id);
    designRef.current = tmpl.design ?? null;
    if (tmpl.subject) setEmailSubject(tmpl.subject);
    const htmlVersion = tmpl.html || "";
    const plainVersion = tmpl.plain_text || (htmlVersion ? convertHtmlToPlainText(htmlVersion) : "");
    setHtmlText(htmlVersion);
    setEditorHtml(htmlVersion);
    setEmailPlainText(plainVersion);
    setHasEditorContent(true);
  };

  // ─── Variable Picker ────────────────────────────────────────────────────────
  const insertVariable = useCallback((rawValue: string) => {
    const variableName = rawValue.trim().replace(/[{}]/g, "");
    if (!variableName) return;
    const placeholder = `{{${variableName}}}`;

    if (activeTab === "editor") {
      // Copied for pasting at the cursor — the editor shows its own toast.
      editorRef.current?.insertVariable(variableName);
    } else {
      setEmailPlainText((prev) => `${prev}${prev ? " " : ""}${placeholder}`);
      toast.success(`Inserted ${placeholder}`);
    }

    setCustomVariable("");
    setShowVariablePicker(false);
  }, [activeTab]);

  // ─── Save ──────────────────────────────────────────────────────────────────
  const handleSaveContent = useCallback(() => {
    if (!emailSubject.trim()) {
      toast.error("Subject is required");
      return;
    }

    const currentHtml = activeTab === "editor" ? (htmlText || editorHtml) : htmlText;
    const finalPlainText = emailPlainText || convertHtmlToPlainText(currentHtml) || "No content available";

    const payload = {
      subject: emailSubject,
      email_header: emailHeader,
      messages: finalPlainText,
      html_text: currentHtml,
      plain_text: finalPlainText,
      template_id: selectedTemplateId || "",
    };

    setCampaignData((prev) => ({ ...prev, ...payload }));
    onSubmit(payload);
  }, [emailSubject, emailHeader, emailPlainText, htmlText, editorHtml, activeTab, convertHtmlToPlainText, setCampaignData, onSubmit, selectedTemplateId]);

  // ─── Preview Check ─────────────────────────────────────────────────────────
  const isPreviewEnabled = (() => {
    const hasSubject = emailSubject.trim().length > 0;
    let hasContent = false;
    if (activeTab === "plain") {
      hasContent = emailPlainText.trim().length > 0;
    } else if (activeTab === "editor") {
      const currentHtml = htmlText || editorHtml;
      hasContent = currentHtml.trim().length > 10 || hasEditorContent;
    } else {
      hasContent = htmlText.trim().length > 0 || hasEditorContent;
    }
    return hasSubject && hasContent;
  })();

  const filteredTemplates = emailTemplates.filter((t) =>
    t.template_name.toLowerCase().includes(templateSearch.toLowerCase())
  );

  const handleConvoSend = async () => "Response from ConvoAI";

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col lg:flex-row items-stretch lg:items-start gap-4">
      {/* Main Area */}
      <div className="flex-1 min-w-0 flex flex-col bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Compose Email</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Compose your email message or select from templates</p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreator(true)}
            className="flex items-center h-10 gap-1.5 px-4 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-gray-900 shadow-sm"
          >
            <Plus className="w-4 h-4" /> Create New Template
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4">
          <div className="flex rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-1 gap-1">
            {(["plain", "editor", "templates"] as TabMode[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all ${
                  activeTab === tab ? "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                }`}
              >
                {tab === "plain" ? "Plain Text" : tab === "editor" ? "Email Editor" : "Templates"}
              </button>
            ))}
          </div>
        </div>

        {/* Subject & Preview */}
        <div className="px-6 pt-4 space-y-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
            <div className="sm:w-28 sm:shrink-0">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Subject</span>
            </div>
            <input
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Enter subject"
              className="flex-1 px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
            <div className="sm:w-28 sm:shrink-0">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Preview Text</span>
            </div>
            <input
              value={emailHeader}
              onChange={(e) => setEmailHeader(e.target.value)}
              placeholder="Enter preview text"
              className="flex-1 px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
          </div>
        </div>

        {/* Variable Picker */}
        {(activeTab === "plain" || activeTab === "editor") && (
          <div className="px-6 pt-3 flex justify-end">
            <button
              onClick={() => setShowVariablePicker(!showVariablePicker)}
              className="flex items-center h-9 gap-1.5 px-3 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-indigo-50"
            >
              <Plus className="w-3.5 h-3.5" /> Add Variables
            </button>
          </div>
        )}

        {/* Variable Picker Dropdown */}
        {showVariablePicker && (
          <div className="px-6 py-2">
            <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Insert variable:</span>
              {EMAIL_VARIABLE_OPTIONS.map((v) => (
                <button
                  key={v}
                  onClick={() => insertVariable(v)}
                  className="px-2.5 py-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-xs text-gray-700 dark:text-gray-300 hover:bg-indigo-50 hover:border-indigo-300"
                >
                  {v}
                </button>
              ))}
              <div className="flex items-center gap-1 ml-2">
                <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">or</span>
                <input
                  value={customVariable}
                  onChange={(e) => setCustomVariable(e.target.value)}
                  placeholder="Custom"
                  className="w-24 px-2 py-1 text-xs border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && customVariable.trim()) {
                      insertVariable(customVariable);
                    }
                  }}
                />
                <button
                  onClick={() => customVariable.trim() && insertVariable(customVariable)}
                  className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div
          className={`px-6 pb-2 pt-3 flex flex-col overflow-x-auto overflow-y-hidden ${viewMode === "mobile" ? "items-center" : ""}`}
          style={{ minHeight: "460px" }}
        >
          {activeTab === "plain" && (
            <PlainTextEditor value={emailPlainText} onChange={setEmailPlainText} />
          )}
          
          {activeTab === "editor" && (
            <div
              className={`flex-1 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden ${viewMode === "mobile" ? "w-[390px]" : "w-full"}`}
              style={{ height: "580px" }}
            >
              <EmailEditorCore
                ref={editorRef}
                minHeight="580px"
                onReady={handleEditorReady}
                onChange={handleEditorChange}
                onTemplateGenerated={handleTemplateGenerated}
              />
            </div>
          )}

          {activeTab === "templates" && (
            <div className="flex flex-col md:flex-row gap-4 flex-1 w-full md:h-[520px]">
              {/* Left: Template list */}
              <div className="w-full md:w-72 md:shrink-0 overflow-auto h-[240px] md:h-[500px] flex flex-col gap-3 pr-1">
                <input
                  type="text"
                  placeholder="Search Templates"
                  value={templateSearch}
                  onChange={(e) => setTemplateSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                />
                {loadingTemplates ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {filteredTemplates.map((t) => (
                      <TemplateCard
                        key={t.id}
                        template={t}
                        isSelected={selectedTemplateId === t.id}
                        onSelect={() => handleSelectEmailTemplate(t)}
                      />
                    ))}
                  </div>
                )}
              </div>
              {/* Right: Preview */}
              <div className="flex-1 h-[320px] md:h-auto rounded-xl border border-gray-200 dark:border-gray-700 overflow-auto bg-white dark:bg-gray-900 p-4">
                {selectedTemplateId ? (
                  <div
                    className="prose prose-sm"
                    dangerouslySetInnerHTML={{
                      __html: emailTemplates.find((t) => t.id === selectedTemplateId)?.html || "",
                    }}
                  />
                ) : (
                  <div className="h-full flex items-center justify-center text-[#7B7B7B] font-semibold dark:text-gray-500">
                    Select a template to preview
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex justify-end items-center">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onBack}
              className="px-5 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-800"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleSaveContent}
              disabled={!isPreviewEnabled}
              className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div
        className="w-full lg:w-80 lg:shrink-0 flex flex-col bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
        style={{ minHeight: "640px" }}
      >
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex justify-between bg-gray-50 dark:bg-gray-800">
          <div className="flex border rounded-lg p-1 bg-white dark:bg-gray-900">
            <button
              onClick={() => setSidebarMode("ai")}
              className={`px-4 py-1.5 text-sm rounded-md ${
                sidebarMode === "ai" ? "bg-indigo-600 text-white" : "text-gray-600 dark:text-gray-300"
              }`}
            >
              ConvoAi
            </button>
            <button
              onClick={() => setSidebarMode("preview")}
              className={`px-4 py-1.5 text-sm rounded-md flex items-center gap-1 ${
                sidebarMode === "preview" ? "bg-indigo-600 text-white" : "text-gray-600 dark:text-gray-300"
              }`}
            >
              <Eye size={16} /> Preview
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          {sidebarMode === "ai" ? (
            <ConvoAIPanel
              isOpen
              onClose={() => {}}
              onSend={handleConvoSend}
              placeholder="Ask anything..."
            />
          ) : (
            <div className="p-4 h-full overflow-auto">
              <EmailMockUp
                subject={emailSubject}
                previewText={emailHeader}
                senderName="VouchLinks Stores"
                htmlContent={activeTab === "editor" ? (htmlText || editorHtml) : htmlText}
                plainText={emailPlainText}
              />
            </div>
          )}
        </div>
      </div>

      {/* Template Creator Modal */}
      <TemplateCreator
        isOpen={showCreator}
        templates={templates}
        onClose={() => setShowCreator(false)}
        onSaveDraft={() => {
          setShowCreator(false);
          fetchTemplates();
        }}
        onSendRequest={() => {
          setShowCreator(false);
          fetchTemplates();
        }}
        initialChannel="email"
      />


      <ScrollToTop />
    </div>
  );
};

export default EmailContentCreation;